import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, AbstractControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { Observable, Subject, Subscription, buffer, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { County } from 'src/app/model/county';
import { Region } from 'src/app/model/region.model';
import { SchoolDistrict } from 'src/app/model/schoolDistrict.model';
import { HelperService } from 'src/app/service/helper.service';
import { DistrictEntityService } from '../../service/district-entity.service';

@Component({
    selector: 'district-dropdown-two-way',
    templateUrl: './district-dropdown-two-way.component.html',
    styleUrl: './district-dropdown-two-way.component.scss'
})
export class DistrictDropdownTwoWayComponent implements OnInit {
    @Input() isMultiple: boolean = true;
    @Input() disableAutoComplete: boolean = false;
    @Input('reset') reset: boolean;
    @Input() required: boolean = false;
    @Input('disabled') disabled: boolean;
    @Input('customLabel') customLabel: string;

    @Input() selectedSchoolDistricts: SchoolDistrict[] = [];
    @Output() selectedSchoolDistrictsChange = new EventEmitter<SchoolDistrict[]>();

    @Input() selectedSchoolDistrict: SchoolDistrict;
    @Output() selectedSchoolDistrictChange = new EventEmitter<SchoolDistrict>();
    @Output() loadingChange = new EventEmitter<boolean>();

    @Input() regions: Region[] = [];
    @Input() counties: County[] = [];

    @ViewChild('filterRef') filterRef: ElementRef;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSelect) matSelect: MatSelect;

    schoolDistrictForm: FormGroup;
    schoolDistrictIds: number[] = [];
    selectedSchoolDistrictIds: number[] = [];

    selectedSchoolDistrictName: string | undefined;
    filteredSchoolDistricts: SchoolDistrict[] = [];
    filteredSchoolDistrictsShow: SchoolDistrict[] = [];

    fieldLabel: string;
    schoolDistricts: SchoolDistrict[] = [];
    allSchoolDistricts: SchoolDistrict[] = [];

    userRegion: Region = new Region();
    userRegionLoaded: boolean = false;
    userRegionSchoolDistricts: SchoolDistrict[] = [];

    emitTimeout: any;
    timeout: any;
    loaded: boolean = false;
    loaded$: Observable<boolean>;
    isLoading: boolean = false;
    changed: boolean = true;

    subs: Subscription[] = [];

    pageIndex: number = 0;
    pageSize: number = 5;

    burstMS: number = 300;
    private changes$ = new Subject<any | null>();
    private destroy$ = new Subject<void>();
    private lastEmitted: any = undefined;
    private hasInitialized = false;

    constructor(
        private fb: FormBuilder,
        private schoolDistrictService: DistrictEntityService
    ) {
        this.schoolDistrictForm = this.fb.group({
            schoolDistrictSelect: [''],
            filter: ['']
        });

        this.loaded$ = schoolDistrictService.loaded$;
        let loadedSub = this.loaded$?.subscribe((loaded) => {
            if (!loaded || this.disabled) {
                this.schoolDistrictSelect.disable();
            } else {
                this.schoolDistrictSelect.enable();
            }
        });
        this.subs.push(loadedSub);

        const burstEnd$ = this.changes$.pipe(debounceTime(this.burstMS));

        this.changes$.pipe(buffer(burstEnd$), takeUntil(this.destroy$)).subscribe((events) => {
            const list = Array.isArray(events) ? events : [events];

            let latestFilters: any = null;
            let latestSelection: any = null;
            let shouldReset = false;

            for (const e of list) {
                if (e?.type === 'filters') latestFilters = e;
                if (e?.type === 'selection') latestSelection = e.value;
                if (e?.type === 'reset') shouldReset = true;
            }

            if (shouldReset) {
                this.deSelectAll();
            }

            if (latestFilters) {
                this.selectedSchoolDistrictIds = [];
                this.schoolDistrictSelect.patchValue([]);

                this.changed = true;
                this.refreshDataRequest();
            }

            if (latestSelection !== null) {
                this.emitNormalized(latestSelection);
            }
        });
    }

    private isNonEmpty(value: any): boolean {
        if (value == null) return false;
        if (Array.isArray(value)) return value.length > 0;
        return true;
    }

    private emitNormalized(value: any) {
        let normalized: number[];

        if (Array.isArray(value)) {
            normalized = value.map((r: any) => r.ID);
        } else if (value && typeof value === 'object') {
            normalized = [value.ID];
        } else if (value != null) {
            normalized = [value];
        } else {
            normalized = [];
        }

        if (this.isSame(normalized, this.lastEmitted)) return;

        this.lastEmitted = normalized;
        this.emit(normalized);
    }

    private isSame(a: any, b: any): boolean {
        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false;
            return a.every((v, i) => v === b[i]);
        }

        return a === b;
    }

    subscribers() {
        let schoolDistrictEntitiesSub = this.schoolDistrictService.entities$.subscribe((schoolDistricts: SchoolDistrict[]) => {
            if (schoolDistricts && schoolDistricts.length > 0) {
                this.allSchoolDistricts = schoolDistricts;
                this.schoolDistrictIds = this.allSchoolDistricts.length > 0 ? this.allSchoolDistricts.map((schoolDistrict) => schoolDistrict.ID!) : [];
                this.changed = true;

                this.loaded = true;
                this.refreshDataRequest();
            }
        });
        this.subs.push(schoolDistrictEntitiesSub);

        let filterInputSub = this.filterInput!.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value: string) => {
            if (value.length === 0) {
                this.filteredSchoolDistricts = this.schoolDistricts;
            } else {
                this.filteredSchoolDistricts = this.schoolDistricts.filter((schoolDistrict) =>
                    schoolDistrict.DISTRICTS.toLowerCase().includes(value.toLowerCase())
                );
                this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });
            }
        });
        this.subs.push(filterInputSub);
    }

    emitSelect(schoolDistrictIds: number[] | number) {
        const ids = Array.isArray(schoolDistrictIds) ? schoolDistrictIds : [schoolDistrictIds];

        // Block empty before initial load
        if (!this.hasInitialized && ids.length === 0) {
            return;
        }

        // Data not ready yet
        if (!this.schoolDistricts.length) {
            this.selectedSchoolDistrictIds = ids;
            return;
        }

        // Replace, don’t merge
        this.selectedSchoolDistrictIds = [...ids];

        const output = this.schoolDistricts.filter((d) => ids.includes(d.ID));

        this.selectedSchoolDistrictName = output[0]?.DISTRICTS;

        this.filterInput?.patchValue('');

        if (output.length) {
            this.goToPage(output[0].ID!);
        } else {
            this.goToPage(0);
        }

        this.selectedSchoolDistrictsChange.emit(output);

        // Allow empty AFTER init
        this.selectedSchoolDistrictChange.emit(output.length ? output[0] : undefined);
    }

    ngOnInit(): void {
        if (this.customLabel) {
            this.fieldLabel = this.customLabel;
        } else {
            this.fieldLabel = 'School District';
        }

        this.subscribers();
    }

    ngOnDestroy() {
        this.subs.forEach((sub) => {
            sub.unsubscribe();
        });

        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.disabled) {
            this.schoolDistrictSelect.disable();
        } else {
            this.schoolDistrictSelect.enable();
        }

        if (changes['reset'] && changes['reset'].currentValue) {
            this.changes$.next({ type: 'reset' });
        }

        if (changes['regions'] || changes['counties']) {
            this.changes$.next({
                type: 'filters',
                regions: this.regions,
                counties: this.counties
            });
        }

        if (changes['selectedSchoolDistricts']) {
            this.changes$.next({
                type: 'selection',
                value: changes['selectedSchoolDistricts'].currentValue
            });
        }

        if (changes['selectedSchoolDistrict']) {
            this.changes$.next({
                type: 'selection',
                value: changes['selectedSchoolDistrict'].currentValue
            });
        }
    }

    emit(ids: any) {
        const normalizedIds = Array.isArray(ids) ? ids : [ids];

        // Preserve intent even if data not ready
        this.selectedSchoolDistrictIds = normalizedIds;

        if (!this.schoolDistricts.length) return;

        this.select(normalizedIds);
        this.emitSelect(normalizedIds);
    }

    refreshDataRequest() {
        if (this.timeout) clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
            if (this.loaded) {
                this.loadData(true);
            }
        }, 100);
    }

    loadData(open: any) {
        if (open) {
            this.filterRef ? this.filterRef.nativeElement.focus() : null;
            if (!this.changed || this.isLoading) return;

            this.schoolDistricts = [];
            this.isLoading = true;
            this.loadingChange.emit(true);
            this.schoolDistrictSelect.disable();
            this.changed = false;
            this.filterInput!.patchValue('');

            let workingRegions = this.removeDuplicateIDs(this.regions);
            let workingSchoolDistricts = this.allSchoolDistricts;
            this.counties = this.removeDuplicateIDs(this.counties);
            let params: any = {};
            params['regions'] = workingRegions.map((region) => region.ID);
            params['counties'] = this.counties.map((county) => county.ID);

            if (this.regions.length > 0) {
                workingSchoolDistricts = workingSchoolDistricts.filter((schoolDistrict) =>
                    this.regions.find((region) => region.ID === schoolDistrict.REGION.ID)
                );
            }

            if (this.counties.length > 0) {
                workingSchoolDistricts = workingSchoolDistricts.filter((schoolDistrict) =>
                    this.counties.find((county) => county.ID === schoolDistrict.COUNTy.ID)
                );
            }

            this.schoolDistricts = Array.from(new Set(workingSchoolDistricts));

            this.schoolDistricts.sort((a, b) => a.DISTRICTS.localeCompare(b.DISTRICTS));
            this.filteredSchoolDistricts = this.schoolDistricts;

            ////console.warn('3 - L - filteredSchoolDistricts (' + this.filteredSchoolDistricts.length + '/' + this.allSchoolDistricts.length + ')');
            if (this.schoolDistricts.length === 1 && !this.disableAutoComplete) {
                this.schoolDistrictSelect.patchValue([]);
                this.selectAll();
                this.emitSelect(this.selectedSchoolDistrictIds);
            } else {
                this.select(this.selectedSchoolDistrictIds);
                this.emitSelect(this.selectedSchoolDistrictIds);
            }
            if (!this.disabled) {
                this.schoolDistrictSelect.enable();
            }
            if (!this.hasInitialized) {
                this.hasInitialized = true;
            }
            ////console.log('selectedSchoolDistrictIds at this pointA is:');
            ////console.log(this.selectedSchoolDistrictIds);
            this.changed = false;
            this.isLoading = false;
            this.loadingChange.emit(false);
        } else {
            ////console.log('selectedSchoolDistrictIds at this pointB is:');
            ////console.log(this.selectedSchoolDistrictIds);

            this.select(this.selectedSchoolDistrictIds);
            this.emitSelect(this.selectedSchoolDistrictIds);
        }
    }

    removeDuplicateIDs(arr: Array<any>): Array<any> {
        arr = arr.filter((value, index, self) => index === self.findIndex((t) => t.place === value.place && t.ID === value.ID));

        return arr;
    }

    select(selection: any) {
        this.filteredSchoolDistricts = this.schoolDistricts;

        if (this.isMultiple) {
            const currentPageIds = this.filteredSchoolDistrictsShow.map((s) => s.ID);

            this.selectedSchoolDistrictIds = this.selectedSchoolDistrictIds.filter((id) => !currentPageIds.includes(id));

            this.selectedSchoolDistrictIds = Array.from(new Set([...this.selectedSchoolDistrictIds, ...selection]));

            const validIds = this.filteredSchoolDistricts.map((s) => s.ID);

            this.selectedSchoolDistrictIds = this.selectedSchoolDistrictIds.filter((id) => validIds.includes(id));

            this.schoolDistrictSelect.patchValue(this.selectedSchoolDistrictIds);
        } else {
            const id = Array.isArray(selection) ? selection[0] : selection;

            this.selectedSchoolDistrictIds = [id];
            this.schoolDistrictSelect.patchValue(id);

            this.goToPage(id);
        }
    }

    selectAll() {
        let selected = this.schoolDistrictSelect.value; // Currently selected

        if (this.isMultiple) {
            selected = Array.isArray(selected) ? selected : [];
            this.selectedSchoolDistrictIds = Array.from(new Set(selected.concat(this.filteredSchoolDistricts.map((schoolDistrict) => schoolDistrict.ID!)))); // Select all filtered (if any filter)
            this.schoolDistrictSelect.patchValue(this.selectedSchoolDistrictIds);
        } else {
            this.selectedSchoolDistrictIds = this.filteredSchoolDistricts.map((schoolDistrict) => schoolDistrict.ID!);
            this.schoolDistrictSelect.patchValue(this.selectedSchoolDistrictIds[0]);
        }
    }

    deSelectAll() {
        //this.reset = false;
        this.schoolDistrictSelect.patchValue([]);
        this.selectedSchoolDistrictIds = [];
        this.filterInput?.patchValue('');
        //this.emitSelect([]);
    }

    close() {
        this.matSelect.close();
    }

    get schoolDistrictSelect(): any {
        return this.schoolDistrictForm.get('schoolDistrictSelect');
    }
    get filterInput(): AbstractControl | null {
        return this.schoolDistrictForm.get('filter');
    }

    onPageChange($event: any) {
        $event.pageIndex = $event.pageIndex < 0 ? 0 : $event.pageIndex;

        this.pageIndex = $event.pageIndex;
        this.pageSize = $event.pageSize;

        this.filteredSchoolDistrictsShow = this.filteredSchoolDistricts.slice(
            $event.pageIndex * $event.pageSize,
            $event.pageIndex * $event.pageSize + $event.pageSize
        );
    }

    goToPage(id: number) {
        let index = this.filteredSchoolDistricts.findIndex((filteredSchoolDistrict) => filteredSchoolDistrict.ID === id);
        let pageIndex = Math.floor(index / this.pageSize);

        if (this.paginator) {
            this.paginator.pageIndex = pageIndex;
        }
        this.onPageChange({ pageIndex: pageIndex, pageSize: this.pageSize });
    }

    getDistrictCount(): number {
        return this.allSchoolDistricts.length;
    }
}
